import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Plus, Trash2, Pause } from "lucide-react";
import {
  getCountryCallingCode,
  parsePhoneNumberFromString,
  type CountryCode,
} from "libphonenumber-js";

import CountryPhoneInput from "../../components/CountryPhoneInput";
import { api } from "../../lib/api";
import type { AdminUser } from "../../lib/types";

export default function AdminUsersPage() {
  const qc = useQueryClient();
  const list = useQuery({
    queryKey: ["admin-users"],
    queryFn: () => api.get<{ items: AdminUser[]; count: number }>("/api/admin/users"),
  });

  const [country, setCountry] = useState<CountryCode>("AE");
  const [national, setNational] = useState("");
  const [form, setForm] = useState({
    name: "",
    email: "",
    role: "hr_viewer" as AdminUser["role"],
  });

  function buildE164(): string {
    const digits = national.replace(/[^\d]/g, "").replace(/^0+/, "");
    if (!digits) return "";
    const candidate = `+${getCountryCallingCode(country)}${digits}`;
    const parsed = parsePhoneNumberFromString(candidate, country);
    return parsed?.isValid() ? parsed.number : candidate;
  }

  const create = useMutation({
    mutationFn: () =>
      api.post<AdminUser>("/api/admin/users", {
        phone_e164: buildE164(),
        name: form.name,
        email: form.email || null,
        role: form.role,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin-users"] });
      setNational("");
      setForm({ name: "", email: "", role: "hr_viewer" });
    },
  });

  const remove = useMutation({
    mutationFn: (phone: string) => api.delete(`/api/admin/users/${encodeURIComponent(phone)}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["admin-users"] }),
  });

  const deactivate = useMutation({
    mutationFn: (phone: string) =>
      api.post(`/api/admin/users/${encodeURIComponent(phone)}/deactivate`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["admin-users"] }),
  });

  return (
    <div className="space-y-3 sm:space-y-4 max-w-3xl">
      <div>
        <h1 className="text-xl sm:text-2xl font-semibold text-slate-100">
          Admin users
        </h1>
        <p className="text-xs sm:text-sm text-slate-400 mt-1">
          Anyone with a row here can sign in via WhatsApp OTP. Super admins
          additionally manage users and reveal sensitive PII.
        </p>
      </div>

      <form
        onSubmit={(e) => {
          e.preventDefault();
          create.mutate();
        }}
        className="rounded-xl bg-[var(--color-surface)]/70 ring-1 ring-[var(--color-border)] p-4 space-y-2"
      >
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          <CountryPhoneInput
            country={country}
            national={national}
            onCountryChange={setCountry}
            onNationalChange={setNational}
          />
          <input
            required
            placeholder="Full name"
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            className="rounded-md bg-slate-900/80 ring-1 ring-slate-700 px-3 py-2 text-sm text-slate-100"
          />
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          <input
            placeholder="email (optional)"
            value={form.email}
            onChange={(e) => setForm({ ...form, email: e.target.value })}
            className="rounded-md bg-slate-900/80 ring-1 ring-slate-700 px-3 py-2 text-sm text-slate-100"
          />
          <select
            value={form.role}
            onChange={(e) => setForm({ ...form, role: e.target.value as AdminUser["role"] })}
            className="rounded-md bg-slate-900/80 ring-1 ring-slate-700 px-2 py-2 text-sm text-slate-100"
          >
            <option value="hr_viewer">HR Viewer</option>
            <option value="super_admin">Super Admin</option>
          </select>
        </div>
        <button
          type="submit"
          disabled={create.isPending || !national || !form.name}
          className="w-full inline-flex items-center justify-center gap-1.5 rounded-md bg-sky-500 hover:bg-sky-400 disabled:opacity-50 text-white px-3 py-2 text-sm font-medium"
        >
          <Plus size={14} /> Add user
        </button>
        <div className="text-xs text-slate-500">
          The system accepts any common phone format — with or without leading 0,
          spaces, or dashes — and saves it in canonical E.164.
        </div>
        {create.isError && (
          <div className="text-sm text-red-300">{(create.error as Error).message}</div>
        )}
      </form>

      <div className="rounded-xl bg-[var(--color-surface)]/70 ring-1 ring-[var(--color-border)] divide-y divide-slate-800">
        {list.isPending ? (
          <div className="p-4 text-sm text-slate-400">Loading…</div>
        ) : (
          list.data?.items.map((u) => (
            <div
              key={u.phone_e164}
              className="p-3 flex flex-wrap sm:flex-nowrap items-start sm:items-center gap-2 sm:gap-3"
            >
              <div className="flex-1 min-w-0 basis-full sm:basis-auto">
                <div className="text-slate-100 truncate">
                  {u.name}{" "}
                  <span className="text-xs text-slate-500 tabular-nums">
                    · {u.phone_e164}
                  </span>
                </div>
                <div className="text-xs text-slate-500 truncate">
                  {u.role === "super_admin" ? "Super Admin" : "HR Viewer"}
                  {u.email ? ` · ${u.email}` : ""}
                  {u.active ? "" : " · inactive"}
                </div>
              </div>
              <button
                onClick={() => deactivate.mutate(u.phone_e164)}
                disabled={!u.active}
                className="text-xs inline-flex items-center gap-1 px-2 py-1.5 rounded bg-slate-800 hover:bg-slate-700 ring-1 ring-slate-700 text-slate-200 disabled:opacity-40"
              >
                <Pause size={12} /> Deactivate
              </button>
              <button
                onClick={() => {
                  if (confirm(`Delete ${u.name}?`)) remove.mutate(u.phone_e164);
                }}
                className="text-xs inline-flex items-center gap-1 px-2 py-1.5 rounded bg-red-500/10 hover:bg-red-500/20 ring-1 ring-red-500/30 text-red-300"
              >
                <Trash2 size={12} /> Delete
              </button>
            </div>
          ))
        )}
      </div>
    </div>
  );
}
